import { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { 
  Home, History, Settings, User, Phone, Mail, Key, Trophy, Flame, 
  ChevronRight, CheckCircle, XCircle, Wallet, Gift, Star, Zap,
  TrendingUp, Shield, Clock, CreditCard, Target, Award, Users,
  Gamepad2, Dice1, CircleDollarSign, Timer, Play, Sparkles
} from "lucide-react";

export default function VulkanBetCombinedPreview() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [browserTheme, setBrowserTheme] = useState('dark');
  const [currentPage, setCurrentPage] = useState('main');
  const [showFrame, setShowFrame] = useState(true);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [listPage, setListPage] = useState(1);
  const ITEMS_PER_PAGE_VB = 12;
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const themeParam = searchParams.get('theme');
    const pageParam = searchParams.get('page');
    const frameParam = searchParams.get('frame');
    if (themeParam) setBrowserTheme(themeParam);
    if (frameParam !== null) setShowFrame(frameParam !== 'false' && frameParam !== '0');
    
    // Auto-detect page from URL path
    const path = location.pathname;
    if (pageParam && ['main', 'history', 'settings'].includes(pageParam)) {
      setCurrentPage(pageParam);
    } else if (path.includes('/history')) {
      setCurrentPage('history');
    } else if (path.includes('/settings') || path.includes('/profile')) {
      setCurrentPage('settings');
    } else {
      setCurrentPage('main');
    }
    // NEW: respect ?filter=withdrawal|deposit|all and ?page=N
    const filterParam = searchParams.get('filter');
    if (filterParam && ['all', 'deposit', 'withdrawal'].includes(filterParam)) {
      setHistoryFilter(filterParam);
    }
    const listPageParam = parseInt(searchParams.get('page') || '1', 10);
    setListPage(Math.max(1, isNaN(listPageParam) ? 1 : listPageParam));
    
    if (dataParam) { try { setData(JSON.parse(decodeURIComponent(dataParam))); } catch (e) { console.error(e); } }
  }, [searchParams, location.pathname]);

  // Set initial date filter from transaction range
  useEffect(() => {
    if (!data || !data.bets || data.bets.length === 0) return;
    const parseDate = (d) => {
      if (!d) return 0;
      const p = d.split('.');
      if (p.length === 3) return new Date(`${p[2]}-${p[1]}-${p[0]}`).getTime();
      return 0;
    };
    const dates = data.bets.map(t => parseDate(t.date)).filter(d => d > 0);
    if (dates.length === 0) return;
    const minD = new Date(Math.min(...dates));
    const maxD = new Date(Math.max(...dates));
    const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    if (!dateFrom) setDateFrom(toISO(minD));
    if (!dateTo) setDateTo(toISO(maxD));
  }, [data]);

  useEffect(() => {
    const titles = { main: 'Главная', history: 'Мои ставки', settings: 'Профиль' };
    document.title = `${titles[currentPage]} | VulkanBet`;
  }, [currentPage]);

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(160deg, #0c0a1a 0%, #1a0e2e 40%, #0d1117 100%)' }}>
      <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  const isDark = browserTheme === 'dark';
  // Всегда светлая тема
  const theme = {
    pageBg: '#f5f7fa',
    sidebarBg: '#ffffff',
    card: 'rgba(255, 255, 255, 0.95)',
    cardHover: '#f1f5f9',
    accent: '#f97316',
    accentGradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    textPrimary: '#0f172a',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    success: '#22c55e',
    danger: '#ef4444',
    frameBg: '#dee1e6',
  };

  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const userId = data.visibleId || Math.floor(100000 + Math.random() * 900000);
  const balance = parseFloat(data.balance || 0);
  const bonusBalance = parseFloat(data.bonusBalance || 0);
  const totalBalance = parseFloat(data.totalBalance || balance + bonusBalance);
  const fmt = (n) => parseFloat(n || 0).toLocaleString('ru-RU');

  // Browser Frame - светлый стиль
  const BrowserFrame = () => (
    <div style={{ backgroundColor: '#dee1e6' }} className="flex-shrink-0">
      <div className="flex items-center h-[38px] pl-2">
        <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] mr-[1px]" 
          style={{ backgroundColor: '#c9cdd4' }}>
          <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2" />
          <span className="text-[13px] text-slate-500">Ozon Карта</span>
        </div>
        <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[180px]" 
          style={{ backgroundColor: '#ffffff' }}>
          <div className="w-4 h-4 mr-2 rounded bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
            <Flame className="w-2.5 h-2.5 text-white" />
          </div>
          <span className="text-[13px] text-slate-800">VulkanBet</span>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-4 gap-4 bg-white">
        <div className="flex gap-2">
          {['←', '→', '↻'].map((a, i) => (
            <button key={i} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 text-slate-500">{a}</button>
          ))}
        </div>
        <div className="flex-1 h-[32px] rounded-full px-4 flex items-center bg-slate-100">
          <span className="text-[14px] text-slate-500">
            vulkanbet.ru/{currentPage === 'main' ? '' : currentPage === 'history' ? 'bets' : 'profile'}
          </span>
        </div>
      </div>
    </div>
  );

  // Sidebar - светлый фон
  const Sidebar = () => (
    <div className="w-64 flex-shrink-0 flex flex-col" 
      style={{ background: '#ffffff', borderRight: '1px solid #e2e8f0' }}>
      {/* Logo */}
      <div className="p-5 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-xl" 
            style={{ background: theme.accentGradient }}>
            <Flame className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="font-bold text-xl text-slate-800">VulkanBet</div>
            <div className="text-xs text-orange-500">Premium Casino</div>
          </div>
        </div>
      </div>

      {/* Balance Card */}
      <div className="p-4">
        <div className="rounded-2xl p-5 relative overflow-hidden" 
          style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%)' }}>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          <div className="relative">
            <div className="text-white/70 text-sm mb-1">Ваш баланс</div>
            <div className="text-3xl font-bold text-white mb-2">{fmt(totalBalance)} ₽</div>
            <div className="flex items-center gap-3 text-sm">
              <div className="flex items-center gap-1 text-white/80">
                <Wallet className="w-4 h-4" /><span>{fmt(balance)} ₽</span>
              </div>
              <div className="flex items-center gap-1 text-yellow-300">
                <Gift className="w-4 h-4" /><span>{fmt(bonusBalance)} ₽</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3">
        {[
          { id: 'main', icon: Gamepad2, label: 'Главная', badge: null },
          { id: 'history', icon: History, label: 'Мои ставки', badge: (data.myBetsCount || (data.bets || []).length) },
          { id: 'settings', icon: Settings, label: 'Профиль', badge: null },
        ].map(item => (
          <button key={item.id} onClick={() => setCurrentPage(item.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl mb-1 transition-all ${
              currentPage === item.id ? 'shadow-lg' : 'hover:bg-slate-100'
            }`}
            style={currentPage === item.id ? { background: theme.accentGradient } : {}}>
            <item.icon className="w-5 h-5" 
              style={{ color: currentPage === item.id ? 'white' : '#64748b' }} />
            <span className="font-medium"
              style={{ color: currentPage === item.id ? 'white' : '#0f172a' }}>{item.label}</span>
            {item.badge > 0 && (
              <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500 text-white">{item.badge}</span>
            )}
          </button>
        ))}
      </nav>

      {/* User Card */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
            style={{ background: theme.accentGradient }}>{accountName.charAt(0)}</div>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate text-slate-800">{accountName}</div>
            <div className="text-xs text-slate-500">ID: {userId}</div>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-500 text-xs font-medium">
            <Star className="w-3 h-3" /> VIP
          </div>
        </div>
      </div>
    </div>
  );

  // Main Page - светлый фон
  const MainPage = () => (
    <div className="flex-1 p-6 overflow-auto" style={{ backgroundColor: '#f5f7fa' }}>
      <div className="rounded-3xl p-8 mb-6 relative overflow-hidden" 
        style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f97316 50%, #eab308 100%)' }}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLThoLTJ2LTRoMnY0eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
        <div className="relative flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-6 h-6 text-yellow-300" />
              <span className="text-white/90 font-medium">Добро пожаловать!</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">{accountName}</h1>
            <p className="text-white/80">Удачи в игре! Ваш бонус ждёт вас.</p>
          </div>
          <div className="text-right">
            <div className="text-white/70 text-sm">Ваш баланс</div>
            <div className="text-4xl font-bold text-white">{fmt(totalBalance)} ₽</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { icon: CreditCard, label: 'Пополнить', color: 'from-green-500 to-emerald-600' },
          { icon: Wallet, label: 'Вывести', color: 'from-blue-500 to-indigo-600' },
          { icon: Gift, label: 'Бонусы', color: 'from-purple-500 to-pink-600' },
          { icon: Trophy, label: 'Турниры', color: 'from-orange-500 to-red-600' },
        ].map((action, i) => (
          <button key={i} className={`rounded-2xl p-5 bg-gradient-to-br ${action.color} text-white hover:scale-105 transition-transform shadow-lg`}>
            <action.icon className="w-8 h-8 mb-3" />
            <div className="font-semibold">{action.label}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 rounded-2xl p-6 bg-white shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold flex items-center gap-2 text-slate-800">
              <Flame className="w-6 h-6 text-orange-500" /> Популярные игры
            </h2>
            <button className="text-sm font-medium text-orange-500">Все игры →</button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              { name: 'Book of Ra', category: 'Слоты', hot: true },
              { name: 'Aviator', category: 'Crash', hot: true },
              { name: 'Blackjack', category: 'Карты', hot: false },
              { name: 'Roulette', category: 'Рулетка', hot: false },
              { name: 'Sweet Bonanza', category: 'Слоты', hot: true },
              { name: 'Poker', category: 'Карты', hot: false },
            ].map((game, i) => (
              <div key={i} className="rounded-xl p-4 cursor-pointer hover:scale-105 transition-transform bg-slate-50 hover:bg-slate-100">
                <div className="w-full h-20 rounded-lg mb-3 flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, hsl(${i * 40 + 10}, 70%, 45%) 0%, hsl(${i * 40 + 40}, 70%, 35%) 100%)` }}>
                  <Dice1 className="w-10 h-10 text-white/80" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-slate-800">{game.name}</div>
                    <div className="text-xs text-slate-500">{game.category}</div>
                  </div>
                  {game.hot && <Flame className="w-4 h-4 text-orange-500" />}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl p-6 bg-white shadow-sm border border-slate-100">
          <h2 className="text-xl font-bold flex items-center gap-2 mb-5 text-slate-800">
            <Zap className="w-6 h-6 text-yellow-500" /> Live ставки
          </h2>
          <div className="space-y-3">
            {(data.liveBets && data.liveBets.length ? data.liveBets : [
              { teams: 'ЦСКА — Спартак', sport: 'Футбол', odds: 2.15, live: true },
              { teams: 'СКА — Динамо', sport: 'Хоккей', odds: 1.85, live: true },
              { teams: 'Зенит — Локо', sport: 'Баскетбол', odds: 1.45, live: false },
              { teams: 'Шахтёр — Ман Сити', sport: 'Футбол', odds: 3.20, live: false },
            ]).map((event, i) => (
              <div key={i} className="rounded-xl p-4 cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${event.live ? 'bg-red-100 text-red-500' : 'bg-slate-200 text-slate-500'}`}>
                    {event.live ? 'LIVE' : event.sport}
                  </span>
                  <span className="font-bold text-orange-500">{event.odds}</span>
                </div>
                <div className="font-medium text-slate-800">{event.teams}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // History Page - Пополнения и Выводы (белый фон)
  const HistoryPage = () => {
    const allTx = data.bets || [];
    
    const parseDate = (d) => {
      if (!d) return 0;
      const p = d.split('.');
      if (p.length === 3) return new Date(`${p[2]}-${p[1]}-${p[0]}`).getTime();
      return 0;
    };

    // Helper: is this a deposit/incoming transaction?
    const isDepositTx = (t) => t.type === 'deposit';
    const isWithdrawalTx = (t) => t.type === 'withdrawal';

    // Filter by type
    let filtered = allTx;
    if (historyFilter === 'deposit') filtered = allTx.filter(isDepositTx);
    else if (historyFilter === 'withdrawal') filtered = allTx.filter(isWithdrawalTx);
    
    // Filter by date
    if (dateFrom) {
      const fromTs = new Date(dateFrom).getTime();
      filtered = filtered.filter(t => parseDate(t.date) >= fromTs);
    }
    if (dateTo) {
      const toTs = new Date(dateTo).getTime() + 86400000;
      filtered = filtered.filter(t => parseDate(t.date) < toTs);
    }

    const deposits = filtered.filter(isDepositTx);
    const withdrawals = filtered.filter(isWithdrawalTx);
    // Stats reflect ALL bets (not filter-scoped) so Пополнения / Выводы cards
    // are always populated regardless of which tab user has selected.
    const globalDeposits = allTx.filter(isDepositTx);
    const globalWithdrawals = allTx.filter(isWithdrawalTx);
    const totalDeposits = globalDeposits.reduce((s, b) => s + parseFloat(b.amount || 0), 0);
    const totalWithdrawals = globalWithdrawals.reduce((s, b) => s + parseFloat(b.amount || 0), 0);

    return (
      <div className="flex-1 p-6 overflow-auto" style={{ backgroundColor: '#f5f7fa' }}>
        <h1 className="text-2xl font-bold mb-6 text-slate-800">История операций</h1>

        {/* Filters */}
        <div className="rounded-2xl p-4 mb-6 bg-white shadow-sm border border-slate-200">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex rounded-lg overflow-hidden border border-slate-200">
              {[
                { id: 'all', label: 'Все' },
                { id: 'deposit', label: 'Пополнения' },
                { id: 'withdrawal', label: 'Выводы' },
              ].map(f => (
                <button key={f.id} onClick={() => setHistoryFilter(f.id)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    historyFilter === f.id 
                      ? 'bg-orange-500 text-white' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}>{f.label}</button>
              ))}
            </div>
            <div className="flex items-center gap-3 ml-auto flex-wrap">
              {/* RU date display (formatted DD.MM.YYYY) */}
              {(() => {
                const toRu = (iso) => {
                  if (!iso) return '—';
                  const p = iso.split('-');
                  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : iso;
                };
                return (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-600">С:</span>
                      <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-700 text-sm min-w-[130px] flex items-center justify-between gap-2">
                        <span>{toRu(dateFrom)}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-400"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-600">По:</span>
                      <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-700 text-sm min-w-[130px] flex items-center justify-between gap-2">
                        <span>{toRu(dateTo)}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-400"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                      </div>
                    </div>
                  </>
                );
              })()}
              <button className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600">Применить</button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Всего', value: filtered.length, color: 'text-slate-800' },
            { label: 'Пополнения', value: `+${fmt(totalDeposits)} ₽`, color: 'text-green-600' },
            { label: 'Выводы', value: `-${fmt(totalWithdrawals)} ₽`, color: 'text-red-500' },
            { label: 'Итого', value: `${totalDeposits - totalWithdrawals >= 0 ? '+' : ''}${fmt(totalDeposits - totalWithdrawals)} ₽`, 
              color: totalDeposits - totalWithdrawals >= 0 ? 'text-green-600' : 'text-red-500' },
          ].map((stat, i) => (
            <div key={i} className="rounded-2xl p-5 bg-white shadow-sm border border-slate-100">
              <div className="text-sm mb-1 text-slate-500">{stat.label}</div>
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl overflow-hidden bg-white shadow-sm border border-slate-100">
          {(() => { /* Pagination slice */ })()}
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <History className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <div className="text-lg font-medium mb-2 text-slate-700">Нет операций</div>
              <div className="text-slate-400">Измените фильтры для отображения</div>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.slice((listPage - 1) * ITEMS_PER_PAGE_VB, listPage * ITEMS_PER_PAGE_VB).map((tx, i) => {
                const isDeposit = isDepositTx(tx);
                return (
                  <div key={i} className="p-5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDeposit ? 'bg-green-100' : 'bg-red-100'}`}>
                        {isDeposit ? <CheckCircle className="w-6 h-6 text-green-500" /> : <XCircle className="w-6 h-6 text-red-500" />}
                      </div>
                      <div>
                        <div className="font-medium text-slate-800">{isDeposit ? 'Пополнение' : 'Вывод средств'}</div>
                        <div className="text-sm text-slate-400">{tx.date} {tx.time}</div>
                      </div>
                    </div>
                    <div className={`text-lg font-bold ${isDeposit ? 'text-green-600' : 'text-red-500'}`}>
                      {isDeposit ? '+' : '-'}{fmt(tx.amount)} ₽
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {filtered.length > 0 && (() => {
            const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE_VB));
            const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
            return (
              <div className="px-5 py-3 flex items-center justify-between text-sm text-slate-500 border-t border-slate-100 bg-slate-50">
                <span>Показано {(listPage - 1) * ITEMS_PER_PAGE_VB + 1}–{Math.min(listPage * ITEMS_PER_PAGE_VB, filtered.length)} из {filtered.length} операций</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setListPage(Math.max(1, listPage - 1))} disabled={listPage === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                  </button>
                  {pages.map(p => (
                    <button key={p} onClick={() => setListPage(p)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${p === listPage ? 'bg-orange-500 text-white' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
                      {p}
                    </button>
                  ))}
                  <button onClick={() => setListPage(Math.min(totalPages, listPage + 1))} disabled={listPage === totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  // Settings/Profile Page - светлый фон
  const SettingsPage = () => (
    <div className="flex-1 p-6 overflow-auto" style={{ backgroundColor: '#f5f7fa' }}>
      <h1 className="text-2xl font-bold mb-6 text-slate-800">Профиль</h1>

      <div className="grid grid-cols-3 gap-6">
        <div className="rounded-2xl p-6 text-center bg-white shadow-sm border border-slate-100">
          <div className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-4xl font-bold text-white"
            style={{ background: theme.accentGradient }}>{accountName.charAt(0)}</div>
          <div className="text-xl font-bold mb-1 text-slate-800">{accountName}</div>
          <div className="text-sm mb-4 text-slate-500">ID: {userId}</div>
          <div className="flex items-center justify-center gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-600 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Верифицирован
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-600 flex items-center gap-1">
              <Star className="w-3 h-3" /> VIP
            </span>
          </div>
        </div>

        <div className="col-span-2 space-y-4">
          {[
            { icon: User, label: 'Полное имя', value: accountName },
            { icon: Mail, label: 'Email', value: data.email || 'Не указан' },
            { icon: Phone, label: 'Телефон', value: data.phone || 'Не указан' },
            { icon: Wallet, label: 'Баланс', value: `${fmt(totalBalance)} ₽` },
            { icon: Gift, label: 'Бонусный счёт', value: `${fmt(bonusBalance)} ₽` },
            { icon: Shield, label: 'Безопасность', value: '2FA включена' },
          ].map((item, i) => (
            <div key={i} className="rounded-xl p-4 flex items-center justify-between bg-white shadow-sm border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-100">
                  <item.icon className="w-5 h-5 text-slate-500" />
                </div>
                <span className="text-slate-500">{item.label}</span>
              </div>
              <span className="font-medium text-slate-800">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen template-preview-scale" style={{ background: theme.pageBg }}>
      {showFrame && <BrowserFrame />}
      <div className="flex" style={{ minHeight: showFrame ? 'calc(100vh - 78px)' : '100vh' }}>
        <Sidebar />
        {currentPage === 'main' && <MainPage />}
        {currentPage === 'history' && <HistoryPage />}
        {currentPage === 'settings' && <SettingsPage />}
      </div>
    </div>
  );
}
